import React, { useState, useRef, useEffect } from 'react';
import { X, CheckCircle, Loader2, Download, Package } from 'lucide-react';
import api from '../utils/api';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { FinancialValue } from './shared/FinancialValue';

/**
 * Bundle Modal - Complete Bundle Purchase Flow
 * 
 * Features:
 * - Bundle preview with pricing breakdown
 * - Stripe payment integration ($200)
 * - PDF generation and download
 * - Success confirmation
 */

interface BundleModalProps {
  isOpen: boolean;
  onClose: () => void;
  claimId: string;
  claimData?: {
    client_name: string;
    total_claim: number;
    event_type?: string;
  };
  demoMode?: boolean;
}

export default function BundleModal({ 
  isOpen, 
  onClose, 
  claimId, 
  claimData,
  demoMode = false 
}: BundleModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  useFocusTrap(modalRef, isOpen);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const [step, setStep] = useState<'preview' | 'payment' | 'generating' | 'success'>('preview');
  const [bundlePath, setBundlePath] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handlePurchase = async () => {
    if (demoMode) {
      // Demo mode: Mock payment and show success
      setStep('generating');
      
      // Simulate bundle generation
      setTimeout(() => {
        setBundlePath('/api/v1/download/sample-pdf'); // Use existing sample PDF
        setStep('success');
      }, 2000);
      
      return;
    }

    // Real mode: Stripe checkout
    setStep('payment');
    
    try {
      // TODO: Implement Stripe checkout
      // For now, proceed to generation
      setStep('generating');
      
      // Call bundle creation API
      const result = await api.post('/api/v1/bundle/create', {
        claim_id: claimId,
        include_cover: true,
        include_toc: true
      });
      
      if (result.success) {
        setBundlePath(result.bundle.bundle_path);
        setStep('success');
      } else {
        throw new Error(result.error || 'Bundle creation failed');
      }
      
    } catch (err: any) {
      setError(err.message);
      setStep('preview');
    }
  };

  const handleDownload = () => {
    if (bundlePath) {
      window.open(bundlePath, '_blank');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div ref={modalRef} role="dialog" aria-modal="true" aria-label="Complete Bundle" className="rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" style={{ backgroundColor: 'var(--color-bg-surface)' }}>

        {/* Header */}
        <div className="p-6 rounded-t-xl" style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <Package className="w-7 h-7" />
                Complete Bundle - $200
              </h2>
              <p className="mt-1" style={{ color: 'var(--color-on-brand)', opacity: 0.85 }}>25-page comprehensive report package</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white/20 p-2 rounded-lg transition"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          
          {/* Preview Step */}
          {step === 'preview' && (
            <div className="space-y-6">
              
              {/* Claim Info */}
              {claimData && (
                <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
                    <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>Claim Details</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p style={{ color: 'var(--color-text-secondary)' }}>Client</p>
                      <p className="font-semibold">{claimData.client_name}</p>
                    </div>
                    <div>
                      <p style={{ color: 'var(--color-text-secondary)' }}>Total Claim</p>
                      <p className="font-semibold"><FinancialValue value={claimData.total_claim} /></p>
                    </div>
                  </div>
                </div>
              )}

              {/* Bundle Contents */}
              <div>
                <h3 className="font-semibold mb-3 text-lg">What's Included</h3>
                <div className="space-y-3">
                  <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--color-bg-base)', border: '1px solid var(--color-border)' }}>
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>Professional Cover Page</p>
                        <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>DisruptionGenius branding with claim details</p>
                      </div>
                      <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>1 page</span>
                    </div>
                  </div>

                  <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--color-bg-base)', border: '1px solid var(--color-border)' }}>
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>Table of Contents</p>
                        <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>Detailed navigation with page numbers</p>
                      </div>
                      <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>1 page</span>
                    </div>
                  </div>

                  <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--color-bg-base)', border: '1px solid var(--color-border)' }}>
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>Standard Standard BI Report</p>
                        <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>Complete calculations, charts, causation tests</p>
                      </div>
                      <div className="text-right">
                        <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>11 pages</span>
                        <p className="text-xs font-semibold" style={{ color: 'var(--color-verified)' }}>FREE</p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--color-bg-base)', border: '1px solid var(--color-border)' }}>
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>AI Legal Narrative</p>
                        <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>Executive summary, causation, attestation</p>
                      </div>
                      <div className="text-right">
                        <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>14 pages</span>
                        <p className="text-xs font-semibold" style={{ color: 'var(--color-needs-human-text)' }}>$150</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Pricing Breakdown */}
              <div className="rounded-lg p-5" style={{ backgroundColor: 'var(--color-needs-human-tint)', border: '2px solid var(--color-needs-human-tint-bd)' }}>
                <h3 className="font-semibold mb-3">Pricing</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--color-text-secondary)' }}>Standard Report</span>
                    <span className="font-mono" style={{ color: 'var(--color-verified)' }}>FREE</span>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--color-text-secondary)' }}>AI Narrative</span>
                    <span className="font-mono">$150.00</span>
                  </div>
                  <div className="flex justify-between" style={{ color: 'var(--color-text-muted)' }}>
                    <span>Bundle Discount</span>
                    <span className="font-mono">-$50.00</span>
                  </div>
                  <div className="pt-2 flex justify-between font-bold text-lg" style={{ borderTop: '2px solid var(--color-needs-human-tint-bd)' }}>
                    <span>Complete Bundle</span>
                    <span className="font-mono" style={{ color: 'var(--color-needs-human-text)' }}>$200.00</span>
                  </div>
                </div>
                <p className="text-xs mt-3 text-center" style={{ color: 'var(--color-text-secondary)' }}>
                  Save $50 compared to purchasing separately
                </p>
              </div>

              {/* Error Message */}
              {error && (
                <div className="rounded-lg p-3 text-sm" style={{ backgroundColor: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)', color: 'var(--color-needs-human-text)' }}>
                  {error}
                </div>
              )}

              {/* Demo Notice */}
              {demoMode && (
                <div className="rounded-lg p-3 text-sm" style={{ backgroundColor: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)', color: 'var(--color-needs-human-text)' }}>
                  <strong>Demo Mode:</strong> This is a demonstration. In production, you'll be charged $200 via Stripe.
                </div>
              )}
            </div>
          )}

          {/* Generating Step */}
          {step === 'generating' && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-16 h-16 animate-spin mb-4" style={{ color: 'var(--color-brand)' }} />
              <p className="text-lg font-semibold">Generating your bundle...</p>
              <p className="text-sm mt-2" style={{ color: 'var(--color-text-secondary)' }}>This may take 10-20 seconds</p>
              <div className="mt-6 space-y-2 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                <p>✓ Merging Standard Standard BI Report</p>
                <p>✓ Merging AI Legal Narrative</p>
                <p>✓ Adding cover page and table of contents</p>
                <p>✓ Creating bookmarks...</p>
              </div>
            </div>
          )}

          {/* Success Step */}
          {step === 'success' && (
            <div className="text-center py-8">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'transparent' }}>
                <CheckCircle className="w-12 h-12" style={{ color: 'var(--color-verified)' }} />
              </div>
              <h3 className="text-2xl font-bold mb-2" style={{ color: 'var(--color-verified)' }}>Bundle Ready!</h3>
              <p className="mb-6" style={{ color: 'var(--color-text-secondary)' }}>
                Your 27-page Complete Bundle has been generated successfully.
              </p>

              {/* Bundle Stats */}
              <div className="rounded-lg p-4 mb-6 inline-block" style={{ backgroundColor: 'var(--color-bg-base)' }}>
                <div className="grid grid-cols-3 gap-6 text-sm">
                  <div>
                    <p style={{ color: 'var(--color-text-secondary)' }}>Pages</p>
                    <p className="font-bold text-xl">27</p>
                  </div>
                  <div>
                    <p style={{ color: 'var(--color-text-secondary)' }}>Sections</p>
                    <p className="font-bold text-xl">4</p>
                  </div>
                  <div>
                    <p style={{ color: 'var(--color-text-secondary)' }}>Price</p>
                    <p className="font-bold text-xl" style={{ color: 'var(--color-needs-human-text)' }}>$200</p>
                  </div>
                </div>
              </div>

              <button
                onClick={handleDownload}
                className="font-bold py-3 px-8 rounded-lg shadow-lg transition flex items-center gap-2 mx-auto"
                style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
              >
                <Download className="w-5 h-5" />
                Download Bundle PDF
              </button>

              <p className="text-xs mt-4" style={{ color: 'var(--color-text-muted)' }}>
                A copy has been sent to your email
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        {step === 'preview' && (
          <div className="p-6 flex justify-between items-center rounded-b-xl" style={{ borderTop: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg-base)' }}>
            <div>
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                ✓ Instant download after purchase
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-6 py-2 rounded-lg hover:bg-[var(--color-bg-hover)] transition"
                style={{ border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
              >
                Cancel
              </button>
              <button
                onClick={handlePurchase}
                className="font-bold px-8 py-2 rounded-lg transition shadow-lg"
                style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
              >
                {demoMode ? 'Generate Bundle (Demo)' : 'Purchase $200'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
